import React from 'react';
import {
    Grid,
    Typography,
    Box,
    Switch,
    TextField,
    CircularProgress,
    FormHelperText
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { FeeScheduleSelection } from "../../../feeSchedulePage/feeScheduleSelectionComponent";
import StateDropdown from '../../RulesEditPage/components/StateDropdown';
import LocalityDropdown from '../../RulesEditPage/components/LocalityDropdown';
import GPCIDropdown from '../../RulesEditPage/components/GPCIDropdown';
import { GPCILocalityData } from '../../../../store/api/globalFeeSchedule';
import TruncatedText from './TruncatedText';

interface FeeScheduleMappingStepProps {
    values: any;
    touched: any;
    errors: any;
    classes: any;
    isCopyNewName: (key: string) => boolean;
    handleCopyModeToggle: (stepKey: string, index: number, checked: boolean, groupPath: string, group: any, setFieldValue: any) => void;
    setFieldValue: (field: string, value: any) => void;
    targetClient: any;
    handleTargetFeeScheduleChange: (setFieldValue: any, groupIndex: number, ruleIndex: number, value: any, type: string) => void;
    handleNewFeeScheduleChange: (setFieldValue: any, groupIndex: number, value: any, type: string) => void;
    handleBlur: (e: any) => void;
    getFeeScheduleHelperText: (touched: any, key: string, index: number, ruleIndex?: number, type?: string) => any;
    getFeeScheduleError: (errors: any, key: string, index: number, ruleIndex?: number, type?: string) => any;
    nameExistErrors: any;
    nameValidatePending: any;
    gpciLocalityData: GPCILocalityData[];
}

const FeeScheduleMappingStep: React.FC<FeeScheduleMappingStepProps> = ({
    values,
    touched,
    errors,
    classes,
    isCopyNewName,
    handleCopyModeToggle,
    setFieldValue,
    targetClient,
    handleTargetFeeScheduleChange,
    handleNewFeeScheduleChange,
    handleBlur,
    getFeeScheduleHelperText,
    getFeeScheduleError,
    nameExistErrors,
    nameValidatePending,
    gpciLocalityData
}) => {
    return (
        <>
            <div style={{ borderBottom:(values?.feeScheduleMapping?.localFeeSchedules?.length > 0 && values?.feeScheduleMapping?.globalFeeSchedules?.length > 0) ? "1px solid lightgray":'none', marginBottom: "20px" }}>
                {values?.feeScheduleMapping?.localFeeSchedules?.length > 0 && <Typography sx={{ fontSize: "20px", fontWeight: "bold", ml: "10px", mb: "20px" }}>Local Fee Schedules</Typography>}

                {values?.feeScheduleMapping?.localFeeSchedules?.map((group, index) => (
                    <Grid container className='pb-4 align-items-start' key={index} sx={{ borderBottom: '1px solid #f0f0f0', pt: 2, px: 1 }}>
                        <Grid item xs={12} sx={{ mb: 1.5 }}>
                            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                                <span className='font-12'><b>Fee Schedule :</b> <TruncatedText text={group.feeScheduleName} fixedWidth={250} /></span>
                                <Box display="flex" alignItems="center" sx={{ ml: 4 }}>
                                    <Typography variant="caption" sx={{ color: isCopyNewName(`step2-local-${index}`) ? 'text.primary' : 'text.secondary', fontWeight: isCopyNewName(`step2-local-${index}`) ? 'normal' : 'bold', mr: 1,fontSize:"13px" }}>Select Fee Schedule At Target</Typography>
                                    <Switch
                                        size="small"
                                        checked={isCopyNewName(`step2-local-${index}`)}
                                        onChange={(e) => handleCopyModeToggle('step2-local', index, e.target.checked, 'feeScheduleMapping.localFeeSchedules', group, setFieldValue)}
                                        color="primary"
                                    />
                                    <Typography variant="caption" sx={{ color: !isCopyNewName(`step2-local-${index}`) ? 'text.primary' : 'text.secondary', fontWeight: !isCopyNewName(`step2-local-${index}`) ? 'normal' : 'bold', ml: 1,fontSize:"13px" }}>Copy Fee Schedule From Source</Typography>
                                </Box>
                            </div>
                        </Grid>
                        <Grid item xs={8}>
                            {/* Iterate Rules within the Group */}
                            {group.rules?.map((rule, ruleIndex) => (
                                <div key={ruleIndex} style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <span className='font-12' style={{ minWidth: '250px', display: 'inline-block', paddingLeft: "20px" }}><b>Rule :</b> <TruncatedText text={rule.ruleName} fixedWidth={180} /></span>

                                    {!isCopyNewName(`step2-local-${index}`) && (
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <FeeScheduleSelection
                                                deletable={rule.targetFeeScheduleId?.id != 0}
                                                feeScheduleType='Local'
                                                hideTypeToggle={true}
                                                targetClient={targetClient?.schema}
                                                defaultValue='Select Fee Schedule At Target'
                                                handleSetFeeSchedule={(rowData) => handleTargetFeeScheduleChange(setFieldValue, index, ruleIndex, rowData, 'localFeeSchedules')}
                                                value={rule.targetFeeScheduleId?.name}
                                            />
                                            {(getFeeScheduleError(errors, 'targetFeeScheduleId', index, ruleIndex, 'localFeeSchedules')) && (
                                                <FormHelperText error style={{ marginLeft: '14px' }}>
                                                    {getFeeScheduleError(errors, 'targetFeeScheduleId', index, ruleIndex, 'localFeeSchedules')}
                                                </FormHelperText>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </Grid>
                        <Grid item xs={4}>
                            {/* New Fee Schedule Name - Shared for the group */}
                            {isCopyNewName(`step2-local-${index}`) && (
                                <Box sx={{ position: 'relative', mt: 4 }}>
                                    <TextField
                                        required
                                        label="New Fee Schedule Name"
                                        name={`feeScheduleMapping.localFeeSchedules.${index}.newFeeScheduleName`}
                                        onChange={e => handleNewFeeScheduleChange(setFieldValue, index, e.target.value, 'localFeeSchedules')}
                                        onBlur={handleBlur}
                                        className={classes.w90}
                                        value={group.newFeeScheduleName}
                                        helperText={(getFeeScheduleHelperText(touched, 'newFeeScheduleName', index, undefined, 'localFeeSchedules') && getFeeScheduleError(errors, 'newFeeScheduleName', index, undefined, 'localFeeSchedules')) || nameExistErrors[`step2-localFeeSchedules-${index}`]}
                                        error={Boolean((getFeeScheduleHelperText(touched, 'newFeeScheduleName', index, undefined, 'localFeeSchedules') && getFeeScheduleError(errors, 'newFeeScheduleName', index, undefined, 'localFeeSchedules')) || nameExistErrors[`step2-localFeeSchedules-${index}`])}
                                    />
                                    {nameValidatePending[`step2-localFeeSchedules-${index}`] === true ? (
                                        <Box className={classes.iconsContainer} sx={{ right: '12%' }}><CircularProgress size={10} /></Box>
                                    ) : nameValidatePending[`step2-localFeeSchedules-${index}`] === false && (
                                        <Box className={classes.iconsContainer} sx={{ right: '12%' }}>
                                            {nameExistErrors[`step2-localFeeSchedules-${index}`] ? <CloseIcon className={classes.errorIcon} /> : <CheckIcon className={classes.successIcon} />}
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* RBRVS Fields - Per Rule */}
                            {group.rules?.map((rule, ruleIndex) => (
                                (rule.isRBRVS || rule?.isDME) && (
                                    <Box key={ruleIndex} sx={{ mt: 1, mb: 4, display: 'flex', flexDirection: 'column', gap: '16px', width: '90%' }}>
                                        <div className='font-12'><b>Rule :</b> <TruncatedText text={rule.ruleName} fixedWidth={180} /></div>
                                        <Box>
                                            <StateDropdown
                                                scheduleId={group.sourceFeeScheduleId}
                                                value={rule.selectedState}
                                                onChange={(value) => {
                                                    setFieldValue(`feeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedState`, value);
                                                    if (value !== rule.selectedState) {
                                                        setFieldValue(`feeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, null);
                                                        setFieldValue(`feeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, null);
                                                    }
                                                }}
                                                onBlur={() => setFieldValue(`feeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedState`, rule.selectedState || '')}
                                                error={getFeeScheduleError(errors, 'selectedState', index, ruleIndex, 'localFeeSchedules') || ""}
                                                required={true}
                                            />
                                        </Box>
                                       {rule.isRBRVS && <Box>
                                            <GPCIDropdown
                                                selectedState={rule.selectedState}
                                                gpciLocalityData={gpciLocalityData}
                                                value={rule.selectedGpci}
                                                onChange={(value) => {
                                                    setFieldValue(`feeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, value);
                                                    if (value !== rule.selectedGpci) {
                                                        setFieldValue(`feeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, null);
                                                    }
                                                }}
                                                onBlur={() => setFieldValue(`feeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, rule.selectedGpci || '')}
                                                error={getFeeScheduleError(errors, 'selectedGpci', index, ruleIndex, 'localFeeSchedules') || ""}
                                                required={true}
                                            />
                                        </Box>}
                                        {rule.isRBRVS && <Box>
                                            <LocalityDropdown
                                                selectedState={rule.selectedState}
                                                selectedGpci={rule.selectedGpci}
                                                gpciLocalityData={gpciLocalityData}
                                                value={rule.selectedLocality}
                                                onChange={(value) => setFieldValue(`feeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, value)}
                                                onBlur={() => setFieldValue(`feeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, rule.selectedLocality || '')}
                                                error={getFeeScheduleError(errors, 'selectedLocality', index, ruleIndex, 'localFeeSchedules') || ""}
                                                required={true}
                                            />
                                        </Box>}
                                    </Box>
                                )
                            ))}
                        </Grid>
                    </Grid>
                ))}
            </div>

            {values?.feeScheduleMapping?.globalFeeSchedules?.length > 0 && <Typography sx={{ fontSize: "20px", fontWeight: "bold", ml: "10px", mb: "20px" }}>Global Fee Schedules</Typography>}
            {values?.feeScheduleMapping?.globalFeeSchedules?.map((group, index) => (
                <Grid container className='pb-4 align-items-start' key={index} sx={{ borderBottom: '1px solid #f0f0f0', pt: 2, px: 1 }}>
                    <Grid item xs={12} sx={{ mb: 1.5 }}>
                        <div style={{ marginTop: '8px' }}>
                            <span className='font-12'><b>Fee Schedule :</b> <TruncatedText text={group.feeScheduleName} fixedWidth={250} /></span>
                        </div>
                    </Grid>
                    
                    <Grid item xs={12}>
                        {/* Iterate Rules within the Group */}
                        {group.rules?.map((rule, ruleIndex) => (
                            <Grid container item xs={12} key={ruleIndex} spacing={2} sx={{ mb: rule.isRBRVS ? 8 : 4, alignItems: rule.isRBRVS ? 'center' : 'start' }}>
                                {/* Left Column: Rule Info */}
                                <Grid item xs={8}>
                                    <div className='font-12' style={{ paddingLeft: "20px" }}>
                                        <b>Rule :</b> <TruncatedText text={rule.ruleName} fixedWidth={180} />
                                    </div>
                                </Grid>

                                {/* Right Column: RBRVS Fields (if applicable) */}
                                <Grid item xs={4}>
                                    {(rule.isRBRVS || rule?.isDME) && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '90%' }}>
                                            <Box>
                                            <StateDropdown
                                                scheduleId={group.sourceFeeScheduleId}
                                                value={rule.selectedState}
                                                onChange={(value) => {
                                                    setFieldValue(`feeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedState`, value);
                                                    if (value !== rule.selectedState) {
                                                        setFieldValue(`feeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, null);
                                                        setFieldValue(`feeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, null);
                                                    }
                                                }}
                                                onBlur={() => setFieldValue(`feeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedState`, rule.selectedState || '')}
                                                error={getFeeScheduleError(errors, 'selectedState', index, ruleIndex, 'globalFeeSchedules') || ""}
                                                required={true}
                                            />
                                        </Box>
                                       {rule.isRBRVS && <Box>
                                            <GPCIDropdown
                                                selectedState={rule.selectedState}
                                                gpciLocalityData={gpciLocalityData}
                                                value={rule.selectedGpci}
                                                onChange={(value) => {
                                                    setFieldValue(`feeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, value);
                                                    if (value !== rule.selectedGpci) {
                                                        setFieldValue(`feeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, null);
                                                    }
                                                }}
                                                onBlur={() => setFieldValue(`feeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, rule.selectedGpci || '')}
                                                error={getFeeScheduleError(errors, 'selectedGpci', index, ruleIndex, 'globalFeeSchedules') || ""}
                                                required={true}
                                            />
                                        </Box>}
                                       {rule.isRBRVS && <Box>
                                            <LocalityDropdown
                                                selectedState={rule.selectedState}
                                                selectedGpci={rule.selectedGpci}
                                                gpciLocalityData={gpciLocalityData}
                                                value={rule.selectedLocality}
                                                onChange={(value) => setFieldValue(`feeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, value)}
                                                onBlur={() => setFieldValue(`feeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, rule.selectedLocality || '')}
                                                error={getFeeScheduleError(errors, 'selectedLocality', index, ruleIndex, 'globalFeeSchedules') || ""}
                                                required={true}
                                            />
                                        </Box>}
                                        </div>
                                    )}
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            ))}

        </>
    );
};

export default FeeScheduleMappingStep;
